// Form contact cascade — reuses proposal contact merge + rep override.

import type { AuthProfile } from "@/hooks/authStateModel";
import type { RepContactLocation } from "@/lib/opportunity/fetchRepContactLocation";
import { getFormTemplateContact } from "./formTemplateContactClient.js";
import {
  mergeProposalContact,
  orgRowToProposalContact,
  type OrgProposalContactRow,
  type ProposalContactInfo,
} from "./proposalContactTypes.js";
import type { FormTemplateContactDef } from "./formContactTypes.js";
import { repProposalContactOverride } from "./repProposalContact.js";

export type ResolvedFormContact = {
  contact: ProposalContactInfo;
  allowRepContactOverride: boolean;
};

function formContactToOverride(contact: FormTemplateContactDef | null | undefined) {
  if (!contact) return null;
  return {
    org_name: contact.org_name,
    person_name: contact.person_name,
    address: contact.address,
    phone: contact.phone,
    email: contact.email,
  };
}

export async function loadFormTemplateContactOverride(
  formTemplateId: string,
): Promise<FormTemplateContactDef | null> {
  const r = await getFormTemplateContact(formTemplateId);
  if (!r.ok || !r.data.contact) return null;
  return r.data.contact;
}

export function resolveFormContact(args: {
  orgRow: OrgProposalContactRow;
  brandNameFallback: string;
  organizationNameFallback?: string;
  templateOverride: FormTemplateContactDef | null | undefined;
  useRepContact: boolean;
  allowRepContactOverride: boolean;
  profile: AuthProfile | null | undefined;
  location: RepContactLocation | null | undefined;
}): ResolvedFormContact {
  const orgBase = orgRowToProposalContact(
    args.orgRow,
    args.brandNameFallback,
    args.organizationNameFallback,
  );
  const merged = mergeProposalContact(orgBase, formContactToOverride(args.templateOverride));
  const allowRep = args.allowRepContactOverride;
  if (allowRep && args.useRepContact) {
    return {
      contact: repProposalContactOverride({
        base: merged,
        profile: args.profile,
        location: args.location,
      }),
      allowRepContactOverride: allowRep,
    };
  }
  return { contact: merged, allowRepContactOverride: allowRep };
}
